import { useRef, useState } from "react";
import css from "./Queues.module.css";
import Intro from "../../../components/intro/Intro";
import NoQueue from "./Demos/NoQueue";
import WithQueue from "./Demos/WithQueue";
import WithFIFOQueue from "./Demos/WithFIFOQueue";
import WithLIFOQueue from "./Demos/WithLIFOQueue";
import Note from "../../../components/note/Note";

const Queues = () => {
  const [example1, setExample1] = useState(false);
  const [example2, setExample2] = useState(false);

  const example1Ref = useRef(null);
  const example2Ref = useRef(null);

  const start1 = () => {
    clearTimeout(example1Ref.current);
    setExample1(true);
    example1Ref.current = setTimeout(() => {
      setExample1(false);
    }, 15000);
  };

  const stop1 = () => {
    clearTimeout(example2Ref.current);
    setExample1(false);
  };

  const start2 = () => {
    clearTimeout(example2Ref.current);
    setExample2(true);
    example2Ref.current = setTimeout(() => {
      setExample2(false);
    }, 15000);
  };

  const stop2 = () => {
    clearTimeout(example2Ref.current);
    setExample2(false);
  };

  return (
    <>
      <Intro
        primary="UNDERSTANDING QUEUES"
        secondary="An interactive tutorial."
      />
      <div className="column p01 g1">
        <h2>QUEUES IN ACTION</h2>
        <p>
          Queues are amazing. The concept is simple enough:{" "}
          <span className="underline">
            whenever there's more work to do than can be done, have the extra
            work wait its turn instead of getting lost.
          </span>{" "}
          When you apply that concept to real-world problems, it's easy to see
          how queues help systems of components work together and scale
          effectively. So, here are some examples:
        </p>
        <h3 className={css.h3}>INTERACTIVE DEMO: NO QUEUE VS. QUEUE</h3>
        <Note type="info">
          <p>
            The following servers have the same capacity: they can process 1
            request at a time, and they take 2 seconds to process a request.
            While a server is busy, it will reject any requests that arrive.
          </p>
          <p>
            Press <span className="white">PLAY</span> to send requests through
            both systems at once and compare what happens when a lot of requests
            arrive within a narrow window of time.
          </p>
        </Note>

        <button
          className={`button ${example1 ? css.stop : css.start}`}
          onClick={example1 ? stop1 : start1}
        >
          {example1 ? "PAUSE" : "PLAY"}
        </button>
        <div className="columns_2">
          <NoQueue isPlaying={example1} />
          <WithQueue isPlaying={example1} />
        </div>
        <ul className="columns_2" style={{ gap: "1rem" }}>
          <Note type="error">
            <p>
              Notice how many requests are denied by the system without a queue.
            </p>
          </Note>
          <Note type="success">
            <p>
              Compare that to the system that uses a queue to buffer its
              traffic.
            </p>
          </Note>
        </ul>

        <h3 className={css.h3}>5 PROBLEMS QUEUES SOLVE</h3>
        <p>
          Imagine owning a popular coffee shop—Queued Coffee Co.—that has
          recently been busier than ever, partially because your competitor,
          Q-Less Coffee, just shut down for good. It didn't surprise you: part
          of their brand involved not using queues or lines of any kind. It was
          quirky, but it consistently caused problems—problems you avoid by
          using queues effectively. When you compare their queue-less system to
          your queued one, it's obvious why they went out of business, and why
          you're doing so well:
        </p>

        <h4 className={css.h4}>BOTTLENECKS</h4>
        <p className={css.description}>
          When components work at different speeds, queues make it so faster
          components aren't stuck waiting for slower ones.
        </p>
        <div className="columns_2">
          <Note type="error">
            <p>
              Q-Less Coffee had their baristas work on one order at a time, from
              start to finish, including taking the order.
            </p>
            <p>
              While one part of an order was in progress, the register sat
              unattended while the employees just stood around waiting. (They
              weren't allowed to work on anything else.)
            </p>
          </Note>
          <Note type="success">
            <p>
              You dedicate some employees as cashiers to take orders and others
              as baristas to fulfill them, so customers can still place orders
              even when you're busy or behind.
            </p>
            <p>
              While one part of an order is in progress (like milk frothing or
              espresso brewing), your baristas immediately start working on
              other tasks, and they're allowed to work on the same order.
            </p>
          </Note>
        </div>
        <h4 className={css.h4}>CAPACITY OVERLOAD</h4>
        <p className={css.description}>
          When more work arrives than a system can process at once, queues
          absorb the excess so the components don't get overwhelmed.
        </p>
        <div className="columns_2">
          <Note type="error">
            <p>
              Q-Less Coffee chose a small location with a limited capacity—that
              they ignored. They didn't use a line (of course), so it was often
              standing-room-only inside, with customers spilling out the door
              and into the street.
            </p>
          </Note>
          <Note type="success">
            <p>
              When your baristas are busy, customers have the option to wait in
              line for their turn, so long as there's room. (You chose a
              spacious location so all customers can wait comfortably inside,
              even during your busiest times.)
            </p>
          </Note>
        </div>
        <h4 className={css.h4}>UNEXPECTED FAILURES</h4>
        <p className={css.description}>
          When a system or request fails, queues hold onto and automatically
          retry related work, preventing data loss even during outages.
        </p>
        <div className="columns_2">
          <Note type="error">
            <p>
              Q-Less Coffee didn't keep any records of their orders, relying
              instead on individual memory. If things went wrong during
              fulfillment, their baristas were prone to forgetting details—or
              even full orders—forcing customers to order again.
            </p>
          </Note>
          <Note type="success">
            <p>
              When your customers place an order, it gets printed onto a ticket
              and pinned to the order rail until it is fulfilled. If things go
              wrong during fulfillment, your baristas handle the issue,
              double-check the rail, and pick up right where they left off.
            </p>
          </Note>
        </div>
        <h4 className={css.h4}>UNFAIR CHAOS</h4>
        <p className={css.description}>
          When there's no agreed-upon order for handling work, queues enforce
          one, ensuring work is handled as effectively as possible.
        </p>
        <div className="columns_2">
          <Note type="error">
            <p>
              Q-Less Coffee didn't use a line, so waiting customers would crowd
              the register and aggressively try to get a barista to take their
              order. Baristas would take the order of whichever customer got
              their attention.
            </p>
          </Note>
          <Note type="success">
            <p>
              When your customers arrive, they wait in line for their turn to be
              served. The line follows a "first in, first out" order, so whoever
              arrives first is served first.
            </p>
          </Note>
        </div>
        <h4 className={css.h4}>WRONG TIMING</h4>
        <p className={css.description}>
          When work is sent before a component that can process it is available,
          queues hold onto the work so the requesting component isn't stuck
          waiting.
        </p>
        <div className="columns_2">
          <Note type="error">
            <p>
              Q-Less Coffee accepted orders by having their customers repeat the
              items they wanted to any barista who was focusing on to them. If
              they didn't get a barista's attention, they had to wait or try
              again.
            </p>
          </Note>
          <Note type="success">
            <p>
              When your customers reach the front of the line, they stay there
              until a cashier has taken their order.
            </p>
          </Note>
        </div>
      </div>

      <div className="background">
        <div className="column p01 g1">
          <h2>QUEUES IN SYSTEM DESIGN</h2>

          <p>
            The same concepts that applied to your coffee shop also apply to
            system design. That's because Queued Coffee Co. <i>is</i> a system,
            with multiple components (baristas, cashiers, coffee machines, the
            line) working together toward a specific goal. And it ran into the
            same 5 problems that all systems run into as they scale
            (bottlenecks, capacity overload, unexpected failures, unfair chaos,
            and wrong timing). In both cases, the solution is conceptually the
            same:{" "}
          </p>
          <p className="underline">
            Give surplus work somewhere safe to wait until it can be processed.
          </p>
          <h3 className={css.h3}>CAPABILITIES</h3>
          <p>
            In the context of system design, the practical implementation of
            queues <span className="underline">decouples</span> the services
            that produce work from the services that consume work, which
            introduces some helpful capabilities:
          </p>
          <ul className="columns_2" style={{ gap: "1rem" }}>
            <div className="card">
              <strong className="ctext">BATCHING</strong>
              <p>
                Consumers can pull requests in groups instead of one at a time,
                cutting the per-message overhead of network calls and database
                requests.
              </p>
            </div>
            <div className="card">
              <strong className="ctext">BROADCASTING</strong>
              <p>
                Multiple consumers can pull requests from a queue or react to
                the same message, enabling work to fan out among several
                services simultaneously.
              </p>
            </div>
            <div className="card">
              <strong className="ctext">INDEPENDENT SCALING</strong>
              <p>
                Producers and consumers don't need to communicate
                synchronously—or even know about each other—enabling them to
                operate and scale independently.
              </p>
            </div>
            <div className="card">
              <strong className="ctext">PRIORITIZATION</strong>
              <p>
                Requests are ordered, and more important tasks can be allowed to
                cut the line to ensure work is processed in the most effective
                order.
              </p>
            </div>
            <div className="card">
              <strong className="ctext">PERSISTENCE</strong>
              <p>
                Requests persist even when consumers are busy or down so work
                isn't lost, and failed requests can be retried automatically.
              </p>
            </div>
            <div className="card">
              <strong className="ctext">THROTTLING</strong>
              <p>
                The rate at which requests are allowed to leave the queue can be
                controlled and customized based on the needs of consumers.
              </p>
            </div>
          </ul>
          <h3 className={css.h3}>CAVEATS</h3>
          <p>
            But of course, queues are not without their tradeoffs. When you
            introduce queues to a system, you also introduce a few other things:
          </p>
          <ul className="columns_2" style={{ gap: "1rem" }}>
            <Note type="warning" header="COMPLEXITY">
              <p>
                The queue itself is another system to build, deploy, monitor,
                and maintain. Development, debugging, and testing get more
                complicated as requests are spread across asynchronous systems.
              </p>
            </Note>
            <Note type="warning" header="COST">
              <p>
                Managed queue services can be expensive, and self-hosted options
                require dedicated infrastructure and operational expertise.
                Either way, you'll be financially responsible for managing the
                queue.
              </p>
            </Note>
            <Note type="warning" header="LATENCY">
              <p>
                Decoupling producers from consumers means the producers no
                longer get an immediate result. Even under ideal conditions, the
                requests will be slower, and the system won't be immediately
                consistent.
              </p>
            </Note>
            <Note type="warning" header="QUEUE LOGIC">
              <p>
                There are several queue-specific details you'll need to figure
                out, including handling failing requests, dead-letter queues,
                unbounded queue growth, and strategies for how requests enter,
                wait in, and leave the queue.
              </p>
            </Note>
          </ul>
          <h3 className={css.h3}>USE CASES</h3>
          <p>
            All things considered, queues are an effective and elegant solution
            to a lot of problems, but they're not always the right tool for the
            job. (Having a poorly-designed or -implemented queue is often worse
            than not having one at all.) It's all about managing the tradeoffs.
            At a high level, here are some common situations where queues
            perform well:
          </p>
          <ul className="columns_2" style={{ gap: "1rem" }}>
            <Note type="success" header="ASYNCHRONOUS PROCESSING">
              <p>
                When you need to respond to a user quickly while a long-running
                job runs in the background.
              </p>
            </Note>
            <Note type="success" header="AUTOMATIC RETRIES">
              <p>
                When you need to automatically retry failed requests and
                gracefully handle requests that repeatedly fail.
              </p>
            </Note>
            <Note type="success" header="CONCURRENT PROCESSING">
              <p>
                When you need to increase throughput by distributing a stream of
                requests among multiple consumers.
              </p>
            </Note>
            <Note type="success" header="DECOUPLING SERVICES">
              <p>
                When you need services to communicate without relying on each
                other or being available at the same time.
              </p>
            </Note>
            <Note type="success" header="FANNING OUT">
              <p>
                When you need one event to trigger two or more independent
                events.
              </p>
            </Note>
            <Note type="success" header="MANAGING TRAFFIC">
              <p>
                When you need to buffer spikes in inbound traffic or control the
                pace of outbound calls.
              </p>
            </Note>
            <Note type="success" header="ORCHESTRATING WORKFLOWS">
              <p>
                When you need to chain multiple processes together and guarantee
                an order of operations.
              </p>
            </Note>
            <Note type="success" header="PRIORITIZING OPERATIONS">
              <p>
                When you need to process urgent work before lower-priority work,
                no matter when it arrives.
              </p>
            </Note>
            <Note type="success" header="SCHEDULING TASKS">
              <p>
                When you need to postpone or precisely time the execution of
                work.
              </p>
            </Note>
          </ul>
        </div>
      </div>

      <div className="column p01 g1">
        <h2>QUEUEING STRATEGIES</h2>
        <p>
          Ultimately, the success of any queue is determined by the details of
          its implementation. There's really no limit to how adaptable (or
          complex) a given implementation can be, but they all share a similar
          framework and approach. In any case, you'll need to consider the
          following approaches and decide which one(s) to use for each stage:
        </p>

        <h3 className={css.h3}>ENTERING</h3>
        <p className={css.description}>
          What should happen when work arrives as the queue fills up?
        </p>
        <div className="columns_2">
          <div className="card">
            <strong className="ctext">BACKPRESSURE</strong>
            <p>
              As the queue nears its capacity, communicate to producers that
              they need to temporarily slow or stop sending data.
            </p>
          </div>
          <div className="card">
            <strong className="ctext">LOAD SHEDDING</strong>
            <p>
              Reject or discard lower-priority work (or drop the oldest items in
              line) to free up capacity for more urgent tasks.
            </p>
          </div>
          <div className="card">
            <strong className="ctext">RANDOM EARLY DETECTION (RED)</strong>
            <p>
              Deny randomly-selected requests in proportion to how full the
              queue is. Optionally target lower-priority requests.
            </p>
          </div>
          <div className="card">
            <strong className="ctext">TAIL DROP</strong>
            <p>
              Simply reject additional incoming requests whenever the queue is
              full.
            </p>
          </div>

          <Note type="error" header="UNBOUNDED">
            <p>
              Let the queue grow without limits—until the system runs out of
              memory and crashes.
            </p>
          </Note>
        </div>

        <h3 className={css.h3}>ORDERING</h3>
        <p className={css.description}>
          When work arrives, which position should it take in line?
        </p>
        <div className="columns_2">
          <div className="card">
            <strong className="ctext">FIRST IN, FIRST OUT (FIFO)</strong>
            <p>Requests are processed in the order they're received.</p>
          </div>
          <div className="card">
            <strong className="ctext">LAST IN, FIRST OUT (LIFO)</strong>
            <p>The requests that arrived most recently are processed first.</p>
          </div>
          <div className="card">
            <strong className="ctext">PRIORITY QUEUEING</strong>
            <p>
              Requests are assigned a priority level and allowed to jump in
              front of requests with lower levels. Levels can change based on
              conditions (like how close a request is to timing out).
            </p>
          </div>
          <div className="card">
            <strong className="ctext">ROUND ROBIN</strong>
            <p>
              Cycle through requests from each producer equally so no one source
              dominates. Weights can be added to prefer certain producers.
            </p>
          </div>
        </div>
        <h4 className={css.h4}>INTERACTIVE DEMO: FIFO VS. LIFO</h4>
        <button
          className={`button ${example2 ? css.stop : css.start}`}
          onClick={example2 ? stop2 : start2}
        >
          {example2 ? "PAUSE" : "PLAY"}
        </button>
        <div className="columns_2">
          <WithFIFOQueue isPlaying={example2} />
          <WithLIFOQueue isPlaying={example2} />
        </div>

        <h3 className={css.h3}>EXITING</h3>
        <p className={css.description}>
          What happens when work is pulled from the queue, processed, or fails?
        </p>
        <div className="columns_2">
          <div className="card">
            <strong className="ctext">ACKNOWLEDGEMENTS</strong>
            <p>
              Consumers signal back to the queue when work is finished. An "ACK"
              (Acknowledgment) removes the item safely, and a "NACK" (Negative
              Acknowledgment) returns the item to the queue to be tried again.
            </p>
          </div>
          <div className="card">
            <strong className="ctext">BATCHING + PREFETCHING</strong>
            <p>
              Consumers pull and process requests in groups. Prefetching keeps
              worker threads constantly fed, trading higher memory usage for
              maximum processing throughput.
            </p>
          </div>

          <div className="card">
            <strong className="ctext">DEAD-LETTER QUEUES</strong>
            <p>
              Isolate repeatedly failing requests into a secondary queue after a
              retry limit is reached to keep the processing pipeline clear and
              allow engineers to debug issues safely.
            </p>
          </div>
          <div className="card">
            <strong className="ctext">RETRIES</strong>
            <p>
              If processing fails, try again after an increasing delay. Add
              jitter (random variation) to prevent consumers from retrying at
              the same time and overwhelming downstream services.
            </p>
          </div>
        </div>

        <h3 className={css.h3}>STRUCTURAL PATTERNS</h3>
        <p className={css.description}>
          How do queues work together with the other components in the system?
        </p>
        <div className="columns_2">
          <div className="card">
            <strong className="ctext">DELAY QUEUES</strong>
            <p>
              Hold requests in a hidden state for a specified duration before
              exposing them to consumers.
            </p>
          </div>
          <div className="card">
            <strong className="ctext">PUB-SUB</strong>
            <p>
              Publish one request to multiple independent queues, allowing
              parallel services to process the event independently.
            </p>
          </div>
          <div className="card">
            <strong className="ctext">WORK QUEUES</strong>
            <p>
              Distribute requests across a group of consumers, ensuring each
              request is processed by exactly one worker.
            </p>
          </div>
        </div>
      </div>
      <div
        className="background"
        style={{ marginBottom: "-6rem", paddingBottom: "6rem" }}
      >
        <div className="column p01 g1">
          <h2>COMING SOON</h2>
          <p>
            This course is still being developed. The core concepts won't
            change, but I might make improvements to the specific wording and
            formatting I use. Plus, I'm actively working on more content:
          </p>

          <div className="columns_2">
            <Note type="success" header="MORE SECTIONS">
              <ul className="list">
                <li>Specific Queueing Technologies</li>
                <li>Real-World Case Studies</li>
                <li>Monitoring Queues</li>
                <li>Basics of Queueing Math</li>
              </ul>
            </Note>
            <Note type="success" header="MORE INTERACTIVE DEMOS">
              <ul className="list">
                <li>Random Early Detection vs. Tail Drop</li>
                <li>FIFO vs. Priority Queueing</li>
                <li>Dead-Letter Queues</li>
                <li>No Backoff vs. Backoff + Jitter</li>
              </ul>
            </Note>
          </div>
          <Note type="success" header="VIDEO WALKTHROUGH">
            <p>
              Prefer to watch? I'll have you covered soon! I have recorded and
              am in the process of editing a video series in which I cover these
              concepts in depth.
            </p>
          </Note>
        </div>
      </div>
    </>
  );
};

export default Queues;
